import React, { useRef } from 'react';
import {
  Card,
  Typography,
  Input,
  Button,
  Space,
  Alert,
  Divider,
  Form,
  Row,
  Col,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useRequest } from '../../hooks/useRequest';
import { useNavigate } from 'react-router-dom';
import { parseIds } from '../../utils/parseIds';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Página para visualizar requisições (CW1).
 * 
 * Permite que o usuário insira uma lista de IDs de requisições para monitoramento.
 * Cada ID deve estar em uma linha separada.
 */
const RequestContent: React.FC = () => {
  const { setRequests } = useRequest();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const requestsIdRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Handler para submeter o formulário.
   */
  const handleSubmit = async (values: { requestsId: string }) => {
    const requestsIdText = values.requestsId || '';
    if (requestsIdText.trim()) {
      const idList = parseIds(requestsIdText);

      if (idList.length > 0) {
        setRequests(idList);
        navigate('/request/monitoring');
      } else {
        setRequests([]);
      }
    }
  };

  /**
   * Exemplos de IDs de requisições para referência.
   */
  const exampleIds = [
    '203f8cfb-2dcc-4231-8ec7-b5c99d13ea8c',
    'f0f58048-fd6d-4819-bf7b-f2bdac851f45',
    '35dc27c5-b4df-4728-ab9d-1ccf36fcbb39',
    '02a89d9a-4ccb-4da8-a0b4-bf0139cf5b96',
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2}>
            <SearchOutlined /> Visualizar Requisições
          </Title>
          <Paragraph>
            Digite os IDs das requisições que deseja monitorar o status e o retorno.
            Você pode usar dois formatos: um ID por linha ou um array JSON.
          </Paragraph>
        </div>

        <Alert
          message="Formatos Aceitos"
          description={
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Text strong>Formato 1 - Múltiplas linhas:</Text>
                  <TextArea
                    value={exampleIds.join("\n")}
                    readOnly
                    rows={4}
                    style={{ fontFamily: "monospace" }}
                  />
                </Space>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Text strong>Formato 2 - Array JSON:</Text>
                  <TextArea
                    value={JSON.stringify(exampleIds, null, 2)}
                    readOnly
                    rows={5}
                    style={{ fontFamily: "monospace" }}
                  />
                </Space>
              </Col>
            </Row>
          }
          type="info"
          showIcon
        />

        <Divider />

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="requestsId"
            label="Lista de IDs de Requisições"
            rules={[
              { required: true, message: 'Por favor, insira pelo menos um ID!' },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length === 0) {
                    return Promise.reject(new Error("Por favor, insira pelo menos um ID"));
                  }
                  const idList = parseIds(value);
                  if (idList.length === 0) {
                    return Promise.reject(new Error("Por favor, insira pelo menos um ID válido"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              ref={requestsIdRef}
              rows={10}
              placeholder='Digite os IDs (um por linha) ou um array JSON: ["id1", "id2", ...]'
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" block>
              Monitorar
            </Button>
          </Form.Item>
          </Form>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página Request.
 */
const Request: React.FC = () => {
  return (
    <RequestContent />
  );
};

export default Request;
